import { computed, DestroyRef, effect, inject, Injectable, Injector, signal, WritableSignal } from '@angular/core';

import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { plainToInstance } from 'class-transformer';
import { debounceTime, EMPTY, filter, finalize, forkJoin, map, Observable, of, pairwise, Subject, switchMap, take, tap } from 'rxjs';

import { QuestionType } from '@empathy/survey/base';
import { Language, LanguagesService, MediaForm, Question, SURVEY_DEFAULT_LANGUAGE_CODE, SurveyModel, Translation, TranslationEntry, TranslationsService } from '@empathy/survey-core';

import { IEditorForm } from '../models/survey-editor/editor-form.interface';
import { SettingsForm } from '../models/survey-editor/settings-form.model';
import { SurveyEditor } from '../models/survey-editor/survey-editor.model';
import { translationGroupKeys } from '../models/translations/translation-groups.constant';
import { questionGroupKeyRegex } from '../models/translations/translation-key.constant';
import { TranslationTabs } from '../models/translations/translation-tabs.enum';
import { QuestionFormInstanceByType } from '../models/type-forms/form-type-instance.constant';
import { IQuestionForm } from '../models/type-forms/question-form-group.interface';

@Injectable({
  providedIn: 'root'
})
export class EditorTranslationsService {
  surveyEditor$ = signal<SurveyEditor | null>(null);

  translations$ = signal<Translation[]>([]);
  translationsLoaded$ = signal(false);

  languages$ = signal<Language[]>([]);
  languagesLoaded$ = signal(false);
  activeTranslationId$ = signal<string | null>(null);
  activeTabIndex$ = signal<number>(TranslationTabs.LanguageSettings);
  activeTranslationGroupKey$ = signal<string>(translationGroupKeys.welcomeScreen);
  translationGroupScrollRequested$ = signal<boolean>(false);
  activeQuestionForm$: WritableSignal<FormGroup> = signal(null);
  welcomeScreenActive$ = signal<boolean>(true);
  endPageScreenActive$ = signal<boolean>(false);
  previewUseDefaultLanguage$ = signal<boolean>(true);

  defaultTranslation$ = computed(() => this.translations$().find((t) => t.code === this.defaultLanguageCode$()));
  localizedTranslations$ = computed(() => this.translations$().filter((t) => t.code !== this.defaultLanguageCode$()));

  defaultLanguageCode$ = inject(SURVEY_DEFAULT_LANGUAGE_CODE);
  defaultTranslationLanguage$ = computed(() => this.languages$().find((l) => l.code === this.defaultLanguageCode$()));

  translatedLanguages$ = computed(() => {
    const codes = new Set(this.translations$().map((t) => t.code));
    return this.languages$().filter((l) => codes.has(l.code));
  });

  activeTranslation$ = computed(() => {
    const id = this.activeTranslationId$();
    return id ? (this.translations$().find((t) => t.id === id) ?? null) : null;
  });

  activeTranslationLanguage$ = computed(() => this.languages$().find((l) => l.code === this.activeTranslation$()?.code));

  editorForm = new FormGroup<IEditorForm>({
    title: new FormControl(''),
    description: new FormControl(''),
    welcomeHtml: new FormControl(''),
    welcomeStimulus: new FormArray([]),
    useEndPageInd: new FormControl(false),
    endPageHtml: new FormControl(''),
    endButtonText: new FormControl('Close Survey'),
    questions: new FormArray<FormGroup<IQuestionForm>>([]),
    questionsHidden: new FormArray<FormGroup<IQuestionForm>>([]),
    pages: new FormArray([]),
    blocks: new FormArray([]),
    sources: new FormArray([]),
    programmerNotes: new FormControl(''),
    settings: new SettingsForm().form,
    loc: new FormArray([])
  });

  private translationsService = inject(TranslationsService);
  private languagesService = inject(LanguagesService);

  private defaultTranslations$ = signal<Map<string, string>>(new Map());
  private researchMethodId: string;

  private translationsEntriesUpdated$ = new Subject<void>();
  private unsavedTranslationIds = new Set<string>();

  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private defaultLanguageLoaded$: Observable<string>;

  constructor() {
    this.defaultLanguageLoaded$ = toObservable(this.defaultLanguageCode$).pipe(filter(Boolean), take(1));

    effect(
      () => {
        const groupKey = this.activeTranslationGroupKey$();

        if (!this.surveyEditor$() || !groupKey) {
          return;
        }

        this.translations$();

        if (groupKey === translationGroupKeys.welcomeScreen || groupKey === translationGroupKeys.thankYouPage) {
          this.setEditorFormTranslations();
          return;
        }

        if (!questionGroupKeyRegex.test(groupKey)) {
          return;
        }

        const form = this.cloneQuestionForm(groupKey);
        this.activeQuestionForm$.set(form);

        if (this.previewUseDefaultLanguage$()) {
          return;
        }

        this.applyActiveTranslationToQuestionForm(groupKey, form);
      },
      { allowSignalWrites: true }
    );

    toObservable(this.surveyEditor$)
      .pipe(
        switchMap((surveyEditor) => {
          if (!surveyEditor) {
            return EMPTY;
          }

          return surveyEditor.form.valueChanges.pipe(
            debounceTime(1000),
            switchMap(() => this.syncWithDefaultTranslations())
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.translationsEntriesUpdated$.pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateUnsavedTranslations());
  }

  init(surveyEditor: SurveyEditor, researchMethodId: string): void {
    this.reset();

    this.surveyEditor$.set(surveyEditor);
    this.researchMethodId = researchMethodId;
    this.translationsLoaded$.set(false);

    this.defaultTranslations$.set(this.buildSurveyTranslationsMap(surveyEditor.model));

    this.loadLanguages();
    this.initTranslations();
    this.initEditorForm();
  }

  addLanguage(languageCode: string): Observable<Translation> {
    const isDefault = languageCode === this.defaultLanguageCode$();
    const translationEntries = this.createTranslationEntries(isDefault);

    return this.translationsService.create(this.researchMethodId, languageCode, translationEntries).pipe(
      tap((translation) => {
        this.translations$.update((translations) => [...translations, translation]);
        this.surveyEditor$().addLanguage(translation.code);

        if (!isDefault) {
          this.activeTranslationId$.set(translation.id);
        }
      })
    );
  }

  removeLanguage(code: string): Observable<void> {
    return this.translationsService.delete(this.researchMethodId, code).pipe(
      tap(() => {
        const wasActive = this.activeTranslation$()?.code === code;

        this.translations$.set(this.translations$().filter((t) => t.code !== code));
        this.surveyEditor$().removeLanguage(code);

        if (wasActive) {
          this.activeTranslationId$.set(this.localizedTranslations$()[0]?.id ?? null);
        }
      })
    );
  }

  setActiveLanguage(languageCode: string): void {
    const translation = this.translations$().find((t) => t.code === languageCode);
    this.activeTranslationId$.set(translation?.id ?? null);
    this.previewUseDefaultLanguage$.set(false);
  }

  setActiveTranslationGroup(groupKey: string, scrollIntoView = false): void {
    this.translationGroupScrollRequested$.set(scrollIntoView);
    this.activeTranslationGroupKey$.set(groupKey);

    const isWelcome = groupKey === translationGroupKeys.welcomeScreen;
    const isThankYou = groupKey === translationGroupKeys.thankYouPage;
    const isQuestionGroup = questionGroupKeyRegex.test(groupKey);

    if (!isQuestionGroup) {
      this.activeQuestionForm$.set(null);
    }

    this.welcomeScreenActive$.set(isWelcome);
    this.endPageScreenActive$.set(isThankYou);
  }

  updateTranslationEntryValue(translationId: string, entryKey: string, entryValue: string): void {
    this.patchTranslationEntries(translationId, (key) => key === entryKey, { value: entryValue, outdatedInd: false });
  }

  setQuestionIgnored(translationId: string, questionId: string, ignored: boolean): void {
    this.patchTranslationEntries(translationId, (key) => key.startsWith(`${questionId}.`), { ignoredInd: ignored });
  }

  setTranslationEntryIgnored(translationId: string, entryKey: string, ignored: boolean): void {
    this.patchTranslationEntries(translationId, (key) => key === entryKey, { ignoredInd: ignored });
  }

  updateTranslationEntries(translationId: string, entries: TranslationEntry[]): void {
    const translation = this.translations$().find((t) => t.id === translationId);

    if (!translation) {
      return;
    }

    const updatedIndex = new Map(entries.map((entry) => [entry.key, entry]));

    const updatedEntries = translation.entries.map((entry) => {
      const updatedEntry = updatedIndex.get(entry.key);

      if (!updatedEntry) {
        return entry;
      }

      return {
        ...entry,
        value: updatedEntry.value ?? '',
        ignoredInd: !!updatedEntry.ignoredInd,
        outdatedInd: false
      };
    });

    this.applyTranslationEntriesUpdate(translationId, updatedEntries);
  }

  translatedCount(translation: Translation): number {
    return translation.entries
      .filter((e) => !e.ignoredInd && !e.outdatedInd)
      .filter((e) => {
        const defaultValue = (this.defaultTranslations$().get(e.key) ?? '').trim();
        const currentValue = (e.value ?? '').trim();

        if (defaultValue.length === 0) {
          return false;
        }

        return currentValue.length > 0;
      }).length;
  }

  missingCount(translation: Translation): number {
    return translation.entries
      .filter((e) => !e.ignoredInd)
      .filter((e) => {
        const defaultValue = (this.defaultTranslations$().get(e.key) ?? '').trim();
        const currentValue = (e.value ?? '').trim();

        return defaultValue.length > 0 && currentValue.length === 0;
      }).length;
  }

  translatedPercentage(translation: Translation): number {
    const translated = this.translatedCount(translation);
    const missing = this.missingCount(translation);
    const ignored = translation.entries.filter((e) => e.ignoredInd).length;
    const outdated = translation.entries.filter((e) => e.outdatedInd).length;

    const total = translated + ignored + missing + outdated;

    if (!total) {
      return 0;
    }

    const completed = translated + ignored;
    return Math.round((completed / total) * 100);
  }

  private reset(): void {
    this.surveyEditor$.set(null);
    this.researchMethodId = null;

    this.translations$.set([]);
    this.translationsLoaded$.set(true);
    this.activeTranslationId$.set(null);
    this.activeTabIndex$.set(TranslationTabs.LanguageSettings);
    this.translationGroupScrollRequested$.set(false);
    this.activeQuestionForm$.set(null);
    this.unsavedTranslationIds.clear();

    this.defaultTranslations$.set(new Map());
  }

  private loadLanguages(): void {
    this.languagesService
      .getAll()
      .pipe(
        tap((languages) => this.languages$.set(languages)),
        finalize(() => this.languagesLoaded$.set(true))
      )
      .subscribe();
  }

  private initTranslations(): void {
    this.defaultLanguageLoaded$
      .pipe(
        switchMap((code) => (this.surveyEditor$().model.loc.length === 0 ? this.initDefaultLanguage(code) : this.loadTranslations(code))),
        finalize(() => this.translationsLoaded$.set(true))
      )
      .subscribe();
  }

  private initDefaultLanguage(code: string): Observable<unknown> {
    const surveyEditor = this.surveyEditor$();
    surveyEditor.addLanguage(code, false);

    toObservable(surveyEditor.isSaving$, { injector: this.injector })
      .pipe(
        pairwise(),
        filter(([wasSaving, isSaving]) => wasSaving && !isSaving),
        take(1),
        switchMap(() => this.createDefaultTranslation()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    return of(void 0);
  }

  private createDefaultTranslation(): Observable<Translation> {
    const code = this.defaultLanguageCode$();
    const entries = this.createTranslationEntries(true);

    return this.translationsService
      .create(this.researchMethodId, code, entries)
      .pipe(tap((translation) => this.translations$.update((translations) => [...translations, translation])));
  }

  private loadTranslations(defaultCode: string): Observable<unknown> {
    const localizations = this.surveyEditor$().model.loc;
    const requests = localizations.map((loc) => this.translationsService.getByResearchMethodAndCode(this.researchMethodId, loc.lc));

    return (requests.length ? forkJoin(requests) : of<Translation[]>([])).pipe(
      tap((translations: Translation[]) => {
        this.translations$.set(translations);

        const first = translations.find((t) => t.code !== this.defaultLanguageCode$()) ?? null;

        if (first) {
          this.activeTranslationId$.set(first.id);
        }
      }),
      switchMap(() => this.ensureDefaultLanguage(defaultCode))
    );
  }

  private ensureDefaultLanguage(code: string): Observable<unknown> {
    return this.translations$().some((t) => t.code === code) ? of(void 0) : this.addLanguage(code);
  }

  private patchTranslationEntries(translationId: string, match: (key: string) => boolean, patch: Partial<TranslationEntry>): void {
    const translation = this.translations$().find((t) => t.id === translationId);

    if (!translation) {
      return;
    }

    const updatedEntries = translation.entries.map((entry) => {
      if (!match(entry.key)) {
        return entry;
      }

      return {
        ...entry,
        ...patch
      };
    });

    this.applyTranslationEntriesUpdate(translationId, updatedEntries);
  }

  private updateUnsavedTranslations(): void {
    for (const translationId of [...this.unsavedTranslationIds]) {
      const t = this.translations$().find((x) => x.id === translationId);

      this.unsavedTranslationIds.delete(translationId);

      if (!t) {
        continue;
      }

      this.translationsService.update(this.researchMethodId, t.code, t.entries).subscribe({
        error: () => {
          this.unsavedTranslationIds.add(translationId);
          this.translationsEntriesUpdated$.next();
        }
      });
    }
  }

  private syncWithDefaultTranslations(): Observable<void> {
    const current = this.buildSurveyTranslationsMap(this.surveyEditor$().model);
    const previous = this.defaultTranslations$();

    const addedKeys: string[] = [];
    const changedKeys: string[] = [];

    for (const [key, value] of current.entries()) {
      if (!previous.has(key)) {
        addedKeys.push(key);
      } else if (previous.get(key) !== value) {
        changedKeys.push(key);
      }
    }

    const removedKeys: string[] = [];
    for (const key of previous.keys()) {
      if (!current.has(key)) {
        removedKeys.push(key);
      }
    }

    if (addedKeys.length === 0 && removedKeys.length === 0 && changedKeys.length === 0) {
      return of(void 0);
    }

    this.defaultTranslations$.set(current);

    const activeGroupKey = this.activeTranslationGroupKey$();

    if (
      activeGroupKey &&
      activeGroupKey !== translationGroupKeys.welcomeScreen &&
      activeGroupKey !== translationGroupKeys.thankYouPage &&
      !current.has(`${activeGroupKey}.title`)
    ) {
      this.setActiveTranslationGroup(translationGroupKeys.welcomeScreen);
    }

    const removedSet = removedKeys.length ? new Set(removedKeys) : null;
    const changedSet = changedKeys.length ? new Set(changedKeys) : null;
    const translations = this.translations$();
    const nextTranslations: Translation[] = new Array(translations.length);
    const changedTranslations: Translation[] = [];
    let anyChanged = false;

    for (let i = 0; i < translations.length; i += 1) {
      const translation = translations[i];
      const isDefault = translation.code === this.defaultLanguageCode$();
      let entries = translation.entries;
      let entriesChanged = false;

      // added
      if (addedKeys.length) {
        const nextEntries = entries.slice();
        for (const key of addedKeys) {
          const value = current.get(key);
          nextEntries.push(this.createTranslationEntry(key, isDefault ? value : ''));
        }
        entries = nextEntries;
        entriesChanged = true;
      }

      // removed
      if (removedSet) {
        const filtered = entries.filter((e) => !removedSet.has(e.key));
        if (filtered.length !== entries.length) {
          entries = filtered;
          entriesChanged = true;
        }
      }

      // changed
      if (changedSet) {
        if (!isDefault) {
          let changedAny = false;
          const updated = entries.map((e) => {
            if (!changedSet.has(e.key)) {
              return e;
            }

            const hasValue = e.value != null && e.value !== '';
            if (e.outdatedInd === hasValue) {
              return e;
            }

            changedAny = true;
            return { ...e, outdatedInd: hasValue };
          });

          if (changedAny) {
            entries = updated;
            entriesChanged = true;
          }
        } else {
          let changedAny = false;
          const updated = entries.map((e) => {
            if (!changedSet.has(e.key)) {
              return e;
            }

            const v = current.get(e.key) ?? '';
            if (e.value === v) {
              return e;
            }

            changedAny = true;
            return { ...e, value: v };
          });

          if (changedAny) {
            entries = updated;
            entriesChanged = true;
          }
        }
      }

      const nextTranslation = entriesChanged ? { ...translation, entries } : translation;
      nextTranslations[i] = nextTranslation;

      if (entriesChanged) {
        changedTranslations.push(nextTranslation);
        anyChanged = true;
      }
    }

    if (anyChanged) {
      this.translations$.set(nextTranslations);

      return forkJoin(changedTranslations.map((t) => this.translationsService.update(this.researchMethodId, t.code, t.entries))).pipe(map(() => void 0));
    }

    return of(void 0);
  }

  private buildSurveyTranslationsMap(survey: SurveyModel): Map<string, string> {
    const translationsMap = new Map<string, string>();

    const set = (key: string, value?: string): Map<string, string> => translationsMap.set(key, value ?? '');

    set(`${translationGroupKeys.welcomeScreen}.title`, survey.title);
    set(`${translationGroupKeys.welcomeScreen}.message`, survey.welcomeHtml);

    set(`${translationGroupKeys.thankYouPage}.button`, survey.endButtonText);
    set(`${translationGroupKeys.thankYouPage}.message`, survey.endPageHtml);

    for (const page of survey.pages ?? []) {
      for (const question of page.questions ?? []) {
        if (question.type !== QuestionType.AIChat) {
          set(`${question.id}.title`, question.title);
        }

        set(`${question.id}.description`, question.description);

        if (question.options) {
          for (const option of question.options) {
            set(`${question.id}.${option.id}.text`, option.text);
          }
        }

        if (question.cards) {
          for (const card of question.cards) {
            set(`${question.id}.${card.id}.text`, card.text);
          }
        }

        if (question.ranks) {
          for (const rank of question.ranks) {
            set(`${question.id}.${rank.id}.text`, rank.text);
          }
        }

        if (question.useOtherInd && question.otherLabel) {
          set(`${question.id}.otherLabel`, question.otherLabel);
        }

        if (question.useNoneInd && question.noneLabel) {
          set(`${question.id}.noneLabel`, question.noneLabel);
        }

        if (question.information) {
          set(`${question.id}.information`, question.information);
        }

        if (question.startingQuestion) {
          set(`${question.id}.startingQuestion`, question.startingQuestion);
        }

        if (question.minValueText) {
          set(`${question.id}.minValueText`, question.minValueText);
        }

        if (question.maxValueText) {
          set(`${question.id}.maxValueText`, question.maxValueText);
        }

        if (question.feedbackMessage) {
          set(`${question.id}.feedbackMessage`, question.feedbackMessage);
        }
      }
    }

    return translationsMap;
  }

  private createTranslationEntries(isDefault: boolean): TranslationEntry[] {
    return [...this.defaultTranslations$().entries()].map(([key, value]) => this.createTranslationEntry(key, isDefault ? value : ''));
  }

  private createTranslationEntry(key: string, value = ''): TranslationEntry {
    return {
      key,
      value,
      ignoredInd: false,
      outdatedInd: false
    };
  }

  private applyTranslationEntriesUpdate(translationId: string, entries: TranslationEntry[]): void {
    this.translations$.update((translations) =>
      translations.map((translation) => {
        if (translation.id !== translationId) {
          return translation;
        }

        return {
          ...translation,
          entries
        };
      })
    );

    this.unsavedTranslationIds.add(translationId);
    this.translationsEntriesUpdated$.next();
  }

  private cloneQuestionForm(id: string): FormGroup<IQuestionForm> | null {
    const src = this.surveyEditor$().questionsCtrls.controls.find((q) => q.get('id').value === id);

    if (!src) {
      return null;
    }

    const srcValue = structuredClone(src.getRawValue());
    const srcQuestion = plainToInstance(Question, srcValue);

    const qForm = QuestionFormInstanceByType(srcValue.type);
    qForm.init(srcQuestion);

    return qForm.form;
  }

  private applyActiveTranslationToQuestionForm(questionId: string, form: FormGroup<IQuestionForm>): void {
    const translation = this.activeTranslation$();

    if (!translation) {
      return;
    }

    this.setControlValue(form, 'title', this.getTranslationValue(translation, `${questionId}.title`));
    this.setControlValue(form, 'description', this.getTranslationValue(translation, `${questionId}.description`));
    this.setControlValue(form, 'otherLabel', this.getTranslationValue(translation, `${questionId}.otherLabel`));
    this.setControlValue(form, 'noneLabel', this.getTranslationValue(translation, `${questionId}.noneLabel`));
    this.setControlValue(form, 'information', this.getTranslationValue(translation, `${questionId}.information`));
    this.setControlValue(form, 'startingQuestion', this.getTranslationValue(translation, `${questionId}.startingQuestion`));
    this.setControlValue(form, 'minValueText', this.getTranslationValue(translation, `${questionId}.minValueText`));
    this.setControlValue(form, 'maxValueText', this.getTranslationValue(translation, `${questionId}.maxValueText`));
    this.setControlValue(form, 'feedbackMessage', this.getTranslationValue(translation, `${questionId}.feedbackMessage`));

    // options
    const options = form.get('options') as FormArray<FormGroup<any>> | null;

    if (options?.controls?.length) {
      for (const optCtrl of options.controls) {
        const optId = optCtrl.get('id')?.value;
        if (!optId) {
          continue;
        }
        this.setControlValue(optCtrl, 'text', this.getTranslationValue(translation, `${questionId}.${optId}.text`));
      }
    }

    // cards
    const cards = form.get('cards') as FormArray<FormGroup<any>> | null;

    if (cards?.controls?.length) {
      for (const cardCtrl of cards.controls) {
        const cardId = cardCtrl.get('id')?.value;
        if (!cardId) {
          continue;
        }
        this.setControlValue(cardCtrl, 'text', this.getTranslationValue(translation, `${questionId}.${cardId}.text`));
      }
    }

    // ranks
    const ranks = form.get('ranks') as FormArray<FormGroup<any>> | null;

    if (ranks?.controls?.length) {
      for (const rankCtrl of ranks.controls) {
        const rankId = rankCtrl.get('id')?.value;
        if (!rankId) {
          continue;
        }
        this.setControlValue(rankCtrl, 'text', this.getTranslationValue(translation, `${questionId}.${rankId}.text`));
      }
    }

    form.updateValueAndValidity({ emitEvent: true });
  }

  private getTranslationValue(translation: Translation, key: string): string | null {
    const entry = translation.entries.find((e) => e.key === key);

    if (entry?.ignoredInd) {
      return null;
    }

    return entry?.value ?? '';
  }

  private setControlValue(group: AbstractControl, path: string, value: string | null): void {
    if (value === null) {
      return;
    }

    const ctrl = group.get(path);
    if (!ctrl) {
      return;
    }

    if (ctrl.value === value) {
      return;
    }
    ctrl.setValue(value, { emitEvent: true });
  }

  private initEditorForm(): void {
    const survey = this.surveyEditor$()?.model;

    if (!survey) {
      return;
    }

    this.editorForm.patchValue({
      settings: survey.settings
    });

    if (survey.welcomeStimulus) {
      this.editorForm.setControl('welcomeStimulus', new FormArray(survey.welcomeStimulus.map((s) => new MediaForm(s).form)));
    }
  }

  private setEditorFormTranslations(): void {
    if (this.previewUseDefaultLanguage$()) {
      const survey = this.surveyEditor$()?.model;

      if (!survey) {
        return;
      }

      this.editorForm.patchValue({
        title: survey.title,
        welcomeHtml: survey.welcomeHtml,
        endPageHtml: survey.endPageHtml,
        endButtonText: survey.endButtonText
      });

      return;
    }

    const translation = this.activeTranslation$();

    if (!translation) {
      return;
    }

    const entriesMap = new Map(translation.entries.map((e) => [e.key, e.value]));

    this.editorForm.patchValue({
      title: entriesMap.get(`${translationGroupKeys.welcomeScreen}.title`) ?? '',
      welcomeHtml: entriesMap.get(`${translationGroupKeys.welcomeScreen}.message`) ?? '',
      endPageHtml: entriesMap.get(`${translationGroupKeys.thankYouPage}.message`) ?? '',
      endButtonText: entriesMap.get(`${translationGroupKeys.thankYouPage}.button`) ?? ''
    });
  }
}
